import { useState } from 'react';
import {
  Calendar, Code, Zap, CheckCircle, ArrowRight,
  Clock, Shield, Cpu, Building2, Sparkles
} from 'lucide-react';
import styles from './Waitlist.module.css';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.vennio.app';

export default function Waitlist() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState(''); // 'coach' or 'developer'
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !role) return;
    setLoading(true); setError(null);
    try {
      const response = await fetch(`${API_URL}/v1/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, role })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to join waitlist');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className={styles.successPage}>
        <div className={styles.successCard}>
          <div className={styles.successIcon}>
            <CheckCircle size={32} aria-hidden="true" />
          </div>
          <h2 className={styles.successTitle}>You're on the list!</h2>
          <p className={styles.successBody}>
            Thanks for joining, {name || 'friend'}. We'll reach out soon with early access.
          </p>
          <div className={styles.successNote}>
            <strong>What's next?</strong>
            <p>Check your email for a confirmation and keep an eye out for updates.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Hero */}
      <div className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.earlyBadge}>
            <Sparkles size={16} aria-hidden="true" />
            Early Access
          </div>
          <h1 className={styles.heroTitle}>The Stripe of Scheduling</h1>
          <p className={styles.heroSubtitle}>
            APIs that make time programmable. Build scheduling into your product in minutes, not months.
          </p>
        </div>

        {/* Two-column layout */}
        <div className={styles.heroGrid}>
          {/* Left: Value props */}
          <div className={styles.valueProps}>
            <div className={styles.valueCard}>
              <h3 className={styles.valueCardTitle}>
                <Zap className={styles.valueTitleIcon} size={20} aria-hidden="true" />
                Why Vennio?
              </h3>
              <ul className={styles.valueList}>
                {[
                  ['Two-sided availability', 'Find times that work for everyone automatically'],
                  ['AI-native design', 'Built for autonomous agents to schedule on behalf of humans'],
                  ['Full lifecycle', 'Create, update, cancel, waitlist — all via API'],
                  ['Real-time webhooks', 'Get notified instantly when bookings change'],
                ].map(([bold, rest]) => (
                  <li key={bold} className={styles.valueItem}>
                    <CheckCircle className={styles.valueCheckIcon} size={18} aria-hidden="true" />
                    <span><strong>{bold}</strong> — {rest}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.statsCard}>
              <h3 className={styles.statsTitle}>Trusted by forward-thinking teams</h3>
              <div className={styles.statsGrid}>
                <div className={styles.statItem}>
                  <div className={styles.statValue}>100%</div>
                  <div className={styles.statLabel}>API uptime</div>
                </div>
                <div className={styles.statItem}>
                  <div className={styles.statValue}>&lt;50ms</div>
                  <div className={styles.statLabel}>Avg response time</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className={styles.formCard}>
            <h2 className={styles.formTitle}>Join the waitlist</h2>
            <p className={styles.formSubtitle}>Get early access and shape the future of scheduling infrastructure.</p>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.roleSection}>
                <label className={styles.roleLabel}>I am a...</label>
                <div className={styles.roleGrid}>
                  <button
                    type="button"
                    onClick={() => setRole('coach')}
                    className={`${styles.roleBtn}${role === 'coach' ? ` ${styles.roleBtnActive}` : ''}`}
                  >
                    <Building2 className={`${styles.roleIcon}${role === 'coach' ? ` ${styles.roleIconActive}` : ''}`} size={24} aria-hidden="true" />
                    <div className={styles.roleName}>Executive Coach</div>
                    <div className={styles.roleDesc}>Consultant, coach, or service provider</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRole('developer')}
                    className={`${styles.roleBtn}${role === 'developer' ? ` ${styles.roleBtnActive}` : ''}`}
                  >
                    <Code className={`${styles.roleIcon}${role === 'developer' ? ` ${styles.roleIconActive}` : ''}`} size={24} aria-hidden="true" />
                    <div className={styles.roleName}>Developer</div>
                    <div className={styles.roleDesc}>Building apps with scheduling</div>
                  </button>
                </div>
              </div>

              <div className={styles.field}>
                <label htmlFor="name" className={styles.fieldLabel}>Name</label>
                <input
                  type="text" id="name"
                  value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Alex Chen" className={styles.input}
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="email" className={styles.fieldLabel}>
                  Email <span className={styles.required}>*</span>
                </label>
                <input
                  type="email" id="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="alex@company.com" className={styles.input}
                />
              </div>

              {error && <div className={styles.errorBox}>{error}</div>}

              <button type="submit" disabled={!email || !role || loading} className={styles.submitBtn}>
                {loading ? (
                  <>
                    <span className={styles.submitSpinner} aria-hidden="true" />
                    Joining...
                  </>
                ) : (
                  <>
                    Join Waitlist
                    <ArrowRight size={18} aria-hidden="true" />
                  </>
                )}
              </button>

              <p className={styles.noSpam}>No spam. We'll only email you about Vennio updates.</p>
            </form>
          </div>
        </div>
      </div>

      {/* Features grid */}
      <div className={styles.features}>
        <div className={styles.featuresInner}>
          <h2 className={styles.featuresTitle}>Everything you need to build scheduling</h2>
          <div className={styles.featuresGrid}>
            {[
              { Icon: Calendar, iconClass: styles.featureIconBlue, bg: styles.featureIconBgBlue, title: 'Calendar Sync', desc: 'Google Calendar & Microsoft 365. Real-time conflict detection.' },
              { Icon: Cpu, iconClass: styles.featureIconPurple, bg: styles.featureIconBgPurple, title: 'AI-Ready APIs', desc: 'OpenAPI spec with AI discovery endpoint. Built for autonomous agents.' },
              { Icon: Shield, iconClass: styles.featureIconGreen, bg: styles.featureIconBgGreen, title: 'Enterprise Ready', desc: 'GDPR compliant. Granular permissions. Webhook signatures.' },
            ].map(({ Icon, iconClass, bg, title, desc }) => (
              <div key={title} className={styles.featureItem}>
                <div className={`${styles.featureIconWrap} ${bg}`}>
                  <Icon className={iconClass} size={24} aria-hidden="true" />
                </div>
                <h3 className={styles.featureTitle}>{title}</h3>
                <p className={styles.featureDesc}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.footerLogo}>
            <Clock size={20} aria-hidden="true" />
            <span className={styles.footerBrand}>Vennio</span>
          </div>
          <p className={styles.footerTagline}>Infrastructure for time. Built for the future of work.</p>
        </div>
      </div>
    </div>
  );
}
